import * as R from "ramda";

import { fetchArtifactsForRag } from "../../../../data/queries/artifacts";
import { countMessageTokens } from "../../../../services/claude";
import { logError } from "../../../../utils/error";
import { ADJACENT_OFFSET, EMPTY_LENGTH } from "../../constants/numeric";
import { getEnlightenMeSystemPrompt } from "../../utils/ragchatStorage";
import {
  ChatActions,
  ChatGetState,
  ChatMessage,
  ChatSetState,
  ChatState,
  ContextDataItem,
  ContextFilter,
  InteractionFilter,
  PendingChunkedQuery
} from "../chatTypes";

type DataLoaderActions = Pick<
  ChatActions,
  | "setContextData"
  | "setPermittedEmails"
  | "setTenantId"
  | "setTotalTokens"
  | "setTotalCalculatedTokens"
  | "setSingleContextTokenLength"
  | "setPendingQuery"
  | "loadContext"
  | "calculateTokens"
  | "resetContext"
>;

const PERCENT_MULTIPLIER = 100;

const nextMessageId = (): string => Date.now().toString();

const updateTokenLimitProperties = (state: ChatState): Pick<ChatState, "exceedsTokenLimit" | "isBlockedByTokenLimit"> => {
  const exceedsTokenLimit = state.totalTokens > state.maxInputTokens;
  const isBlockedByTokenLimit = exceedsTokenLimit && !state.isChunkingEnabled;
  return { exceedsTokenLimit, isBlockedByTokenLimit };
};

// Bedrock CountTokens dispatches against Sonnet 4.6 with the long-context
// beta (1M-token ceiling) and throws ValidationException for inputs above
// that, with no opt-out. When that happens, surface 1M as the count — it
// routes the user through the existing exceedsTokenLimit UI flow (1M is
// always above maxInputTokens) and keeps the displayed token / percentage
// numbers sane instead of leaking a sentinel value into the system message.
const COUNT_TOKENS_HARD_LIMIT = 1000000;

const applyTotalTokensUpdate = (set: ChatSetState, tokens: number): void => {
  set((state) => {
    const newState = { ...state, totalTokens: tokens };
    return { ...newState, ...updateTokenLimitProperties(newState) };
  });
};

const applyResetContextUpdate = (set: ChatSetState): void => {
  set((state) => {
    const newState: ChatState & ChatActions = {
      ...state,
      contextData: null,
      contextChunks: [],
      isContextLoaded: false,
      lastSelectedContext: { interactionIds: [], filter: null },
      totalTokens: EMPTY_LENGTH
    };
    return { ...newState, ...updateTokenLimitProperties(newState) };
  });
};

const upsertSystemMessage = (get: ChatGetState, message: ChatMessage, keepExisting: boolean): void => {
  if (!keepExisting) {
    get().setMessages([message]);
    return;
  }

  const { messages } = get();
  const lastMessage = messages[messages.length - ADJACENT_OFFSET];
  if (lastMessage?.role === "system") {
    const { id: _id, ...updates } = message;
    get().updateMessage(lastMessage.id, updates);
    return;
  }
  get().addMessage(message);
};

const buildSystemMessage = (content: string, extras?: Partial<ChatMessage>): ChatMessage => ({
  id: nextMessageId(),
  content,
  role: "system",
  timestamp: new Date(),
  ...extras
});

const buildErrorMessage = (error: unknown): ChatMessage => {
  const errorMessage = error instanceof Error ? error.message : "An error occurred loading context data";
  return {
    id: nextMessageId(),
    content: `Error: ${errorMessage}`,
    role: "error",
    timestamp: new Date()
  };
};

interface ContextLoadedContentParams {
  keepExistingMessages: boolean;
  artifactCount: number;
  tokens: number;
  tokenPercentage: number;
  nextStepMessage: string;
}

const buildContextLoadedContent = (params: ContextLoadedContentParams): string => {
  const { keepExistingMessages, artifactCount, tokens, tokenPercentage, nextStepMessage } = params;
  const verb = keepExistingMessages ? "refreshed" : "loaded";
  return `Context ${verb} with ${artifactCount} artifacts (${tokens.toLocaleString()} tokens, ${tokenPercentage}% of limit). ${nextStepMessage}`;
};

const buildContextFilter = (
  interactionFilter: InteractionFilter | undefined,
  artifactCount: number,
  tokens: number
): ContextFilter | undefined =>
  interactionFilter ? { ...interactionFilter, artifactCount, totalTokens: tokens } : undefined;

interface LoadSuccessParams {
  data: ContextDataItem[];
  interactionIds: string[];
  interactionFilter?: InteractionFilter;
  keepExistingMessages: boolean;
  isChunkingEnabled: boolean;
}

const handleLoadSuccess = async (get: ChatGetState, set: ChatSetState, params: LoadSuccessParams): Promise<void> => {
  const { data, interactionIds, interactionFilter, keepExistingMessages, isChunkingEnabled } = params;

  set({
    contextData: data,
    isContextLoaded: true,
    lastSelectedContext: { interactionIds, filter: interactionFilter ?? null }
  });

  const tokens = await get().calculateTokens();

  if (data.length > EMPTY_LENGTH) {
    get().setTotalCalculatedTokens(tokens);
    get().setSingleContextTokenLength(EMPTY_LENGTH);
  }

  const tokenLimit = get().maxInputTokens;
  const tokenPercentage = Math.round((tokens / tokenLimit) * PERCENT_MULTIPLIER);
  const exceedsLimit = tokens > tokenLimit;
  const artifactCount = data.length;
  const nextStepMessage = exceedsLimit && !isChunkingEnabled
    ? "You are over your token limit. Either adjust your filters or remove some data from the context."
    : "You can now ask questions about this data.";

  const messageContent = buildContextLoadedContent({
    keepExistingMessages,
    artifactCount,
    tokens,
    tokenPercentage,
    nextStepMessage
  });

  upsertSystemMessage(
    get,
    buildSystemMessage(messageContent, {
      exceedsTokenLimit: exceedsLimit,
      contextData: data,
      contextFilter: buildContextFilter(interactionFilter, artifactCount, tokens)
    }),
    keepExistingMessages
  );
};

interface LoadEmptyParams {
  interactionIds: string[];
  interactionFilter?: InteractionFilter;
  keepExistingMessages: boolean;
}

const handleLoadEmptyResult = (get: ChatGetState, set: ChatSetState, params: LoadEmptyParams): void => {
  const { interactionIds, interactionFilter, keepExistingMessages } = params;
  const noArtifactsMessage = buildSystemMessage(
    "No artifacts found with the current filters. Please adjust your filters to continue."
  );
  upsertSystemMessage(get, noArtifactsMessage, keepExistingMessages);

  set({
    contextData: [],
    isContextLoaded: true,
    lastSelectedContext: { interactionIds, filter: interactionFilter ?? null }
  });
};

interface LoadErrorParams {
  error: unknown;
  keepExistingMessages: boolean;
}

const handleLoadError = (get: ChatGetState, set: ChatSetState, params: LoadErrorParams): void => {
  const errorMsg = buildErrorMessage(params.error);
  const addErrorMessage = R.ifElse(
    R.always(params.keepExistingMessages),
    () => get().addMessage(errorMsg),
    () => get().setMessages([errorMsg])
  );
  addErrorMessage();
  applyResetContextUpdate(set);
};

interface LoadContextParams {
  interactionIds: string[];
  keepExistingMessages: boolean;
  interactionFilter?: InteractionFilter;
}

const executeLoadContext = async (get: ChatGetState, set: ChatSetState, params: LoadContextParams): Promise<void> => {
  const { interactionIds, keepExistingMessages, interactionFilter } = params;
  const { permittedEmails, isChunkingEnabled, includedArtifactTypes } = get();

  try {
    const result = await fetchArtifactsForRag(
      {
        page: 0,
        pageSize: interactionIds.length,
        permittedEmails: permittedEmails ?? [],
        interactionIds
      },
      includedArtifactTypes
    );

    if (result.error) {
      handleLoadError(get, set, { error: result.error, keepExistingMessages });
      return;
    }

    const hasNoData = !result.data || R.isEmpty(result.data);
    if (hasNoData) {
      handleLoadEmptyResult(get, set, { interactionIds, interactionFilter, keepExistingMessages });
      return;
    }

    await handleLoadSuccess(get, set, {
      data: result.data as ContextDataItem[],
      interactionIds,
      interactionFilter,
      keepExistingMessages,
      isChunkingEnabled
    });
  } catch (error) {
    handleLoadError(get, set, { error, keepExistingMessages });
  }
};

const executeCalculateTokens = async (get: ChatGetState, set: ChatSetState): Promise<number> => {
  const { contextData, model } = get();

  if (!contextData || contextData.length === EMPTY_LENGTH) {
    applyTotalTokensUpdate(set, EMPTY_LENGTH);
    return EMPTY_LENGTH;
  }

  try {
    const tokenCountResult = await countMessageTokens({
      context: contextData,
      query: undefined,
      systemPrompt: getEnlightenMeSystemPrompt(),
      model
    });

    if (tokenCountResult.error) {
      logError("Token count returned error, using hard limit", tokenCountResult.error);
    }

    const tokens = tokenCountResult.error
      ? COUNT_TOKENS_HARD_LIMIT
      : tokenCountResult.tokenCount ?? EMPTY_LENGTH;
    applyTotalTokensUpdate(set, tokens);
    return tokens;
  } catch (error) {
    logError("Error calculating tokens", error);
    applyTotalTokensUpdate(set, EMPTY_LENGTH);
    return EMPTY_LENGTH;
  }
};

export const createDataLoaderActions = (set: ChatSetState, get: ChatGetState): DataLoaderActions => ({
  setContextData: (data: ContextDataItem[] | null): void => {
    set({ contextData: data });
  },

  setPermittedEmails: (emails: string[] | null): void => {
    set({ permittedEmails: emails });
  },

  setTenantId: (tenantId: string | null): void => {
    set({ tenantId });
  },

  setTotalTokens: (tokens: number): void => {
    applyTotalTokensUpdate(set, tokens);
  },

  setTotalCalculatedTokens: (tokens: number): void => {
    set({ totalCalculatedTokens: tokens });
  },

  setSingleContextTokenLength: (tokens: number): void => {
    set({ singleContextTokenLength: tokens });
  },

  setPendingQuery: (query: PendingChunkedQuery | null): void => {
    set({ pendingQuery: query });
  },

  loadContext: async (
    interactionIds: string[],
    keepExistingMessages: boolean = false,
    interactionFilter?: InteractionFilter
  ): Promise<void> => {
    get().setLoading(true);
    await executeLoadContext(get, set, { interactionIds, keepExistingMessages, interactionFilter });
    get().setLoading(false);
  },

  calculateTokens: async (): Promise<number> => executeCalculateTokens(get, set),

  resetContext: (): void => {
    applyResetContextUpdate(set);
  }
});
